/**
 * ClientGroup Cache — Incremental fetch helper for ClientGroups.
 * 
 * Stores resolved ClientGroups in DataCache during post-login survey loading.
 * When new ClientGroup IDs are encountered that are not already cached, fetches
 * only the missing IDs and merges them into the existing cache.
 * 
 * All components requesting ClientGroups receive cached data without additional API calls.
 * 
 * @module clientGroupCache
 * 
 * Validates: Requirements 5.1, 5.2, 5.3
 */

import dataCache from './dataCache.js';

const CACHE_COLLECTION = 'ClientGroups';
const CACHE_OPERATION = 'read';
const CACHE_PARAMS = { ids: 'all' };
const STALE_TIME = 86400000; // 24 hours — ClientGroups rarely change

/**
 * Get ClientGroups by IDs with incremental fetch.
 * Only fetches IDs not already cached; merges results into DataCache.
 * 
 * @param {string[]} ids - Array of ClientGroup $oid strings to resolve
 * @param {Object} db - DatabaseService instance (with .collection() method)
 * @returns {Promise<Object>} Map of id → ClientGroup object
 */
export async function getClientGroups(ids, db) {
  if (!ids || ids.length === 0) {
    return {};
  }

  // Deduplicate requested IDs
  const uniqueIds = [...new Set(ids.filter(Boolean))];

  // Get currently cached groups from DataCache
  const cacheKey = dataCache._generateKey(CACHE_COLLECTION, CACHE_OPERATION, CACHE_PARAMS);
  const cachedEntry = dataCache._cache.get(cacheKey);
  const cachedGroups = cachedEntry ? { ...cachedEntry.data } : {};

  // Find IDs not already in cache
  const missingIds = uniqueIds.filter(id => !cachedGroups[id]);

  if (missingIds.length === 0) {
    // All requested groups are already cached — return from cache
    const result = {};
    uniqueIds.forEach(id => {
      if (cachedGroups[id]) result[id] = cachedGroups[id];
    });
    return result;
  }

  // Fetch only the missing ClientGroups from the API
  const newGroups = await db.collection('ClientGroups').find(
    { _id: { $in: missingIds.map(id => ({ "$oid": id })) } },
    { name: 1, suburb: 1, postcode: 1, _chunkSize: 500 }
  );

  // Convert fetched array to a map keyed by $oid
  const newGroupsMap = {};
  if (Array.isArray(newGroups)) {
    newGroups.forEach(g => {
      const id = g._id && g._id["$oid"] ? g._id["$oid"] : (g._id ? g._id.toString() : null);
      if (id) {
        newGroupsMap[id] = g;
      }
    });
  }

  // Merge new groups into existing cached data
  const merged = { ...cachedGroups, ...newGroupsMap };

  // Store the merged result back in DataCache with 24h stale time
  dataCache.set(CACHE_COLLECTION, CACHE_OPERATION, CACHE_PARAMS, merged, { staleTime: STALE_TIME });

  // Return only the requested groups (subset of merged)
  const result = {};
  uniqueIds.forEach(id => {
    if (merged[id]) result[id] = merged[id];
  });
  return result;
}

/**
 * Store pre-fetched ClientGroups directly into the cache.
 * Used during post-login survey loading to populate the cache with initial data.
 * 
 * @param {Array} clientGroupsArray - Array of ClientGroup documents from the API
 */
export function storeClientGroups(clientGroupsArray) {
  if (!Array.isArray(clientGroupsArray) || clientGroupsArray.length === 0) {
    return;
  }

  // Get existing cached groups
  const cacheKey = dataCache._generateKey(CACHE_COLLECTION, CACHE_OPERATION, CACHE_PARAMS);
  const cachedEntry = dataCache._cache.get(cacheKey);
  const cachedGroups = cachedEntry ? { ...cachedEntry.data } : {};

  // Convert array to map and merge
  clientGroupsArray.forEach(g => {
    const id = g._id && g._id["$oid"] ? g._id["$oid"] : (g._id ? g._id.toString() : null);
    if (id) {
      cachedGroups[id] = g;
    }
  });

  // Store merged result
  dataCache.set(CACHE_COLLECTION, CACHE_OPERATION, CACHE_PARAMS, cachedGroups, { staleTime: STALE_TIME });
}

/**
 * Resolve an array of ClientGroup IDs to their full objects using the cache.
 * Convenience method that returns an array (preserving order) rather than a map.
 * 
 * @param {string[]} ids - Array of ClientGroup $oid strings
 * @param {Object} db - DatabaseService instance
 * @returns {Promise<Array>} Array of ClientGroup objects (in same order as input IDs, nulls filtered)
 */
export async function resolveClientGroups(ids, db) {
  const groupMap = await getClientGroups(ids, db);
  return ids.map(id => groupMap[id]).filter(Boolean);
}

/**
 * Get a single ClientGroup by ID from cache, fetching if needed.
 * 
 * @param {string} id - ClientGroup $oid string
 * @param {Object} db - DatabaseService instance
 * @returns {Promise<Object|null>} The ClientGroup object or null if not found
 */
export async function getClientGroup(id, db) {
  if (!id) return null;
  const groupMap = await getClientGroups([id], db);
  return groupMap[id] || null;
}

export default getClientGroups;
